import React from "react";
import { distanceDateFormat } from "../../common/utils/dateformat";
import {
  EventType,
  EventHistogramInterval,
  EventHistogramRange,
  EventHistogramUnique,
  useEventSummaryQuery,
} from "../../graphql/generated";
import { Line } from "react-chartjs-2";
import { ChartData } from "chart.js";
import "chart.js/auto";
import "chartjs-adapter-date-fns";
import Loading from "../../common/components/loading";
import { Transition } from "@headlessui/react";
import Error from "../../common/components/error";

const DashboardTours: React.FC<{
  range: EventHistogramRange;
  interval: EventHistogramInterval;
}> = ({ range, interval }) => {
  const [lastUpdated, setLastUpdated] = React.useState(new Date());

  const [{ data, error, fetching }, executeQuery] = useEventSummaryQuery({
    variables: {
      eventType: EventType.TourCreated,
      interval: interval,
      range: range,
      uniqueBy: EventHistogramUnique.User,
    },
  });

  const [{ data: data2, fetching: fetching2 }, executeQuery2] =
    useEventSummaryQuery({
      variables: {
        eventType: EventType.ListingsRequestTour,
        interval: interval,
        range: range,
        uniqueBy: EventHistogramUnique.User,
      },
    });

  const chartData = React.useMemo<ChartData<"line", number[], Date>>(() => {
    return {
      labels: data?.eventSummary.agent.map((m) => new Date(m.bucketDate)),
      datasets: [
        {
          label: "Tours Created",
          data: data ? data?.eventSummary.agent.map((m) => m.totalCount) : [],
          borderColor: "rgb(239, 68, 68)",
          backgroundColor: "rgba(239, 68, 68, 0.5)",
        },
        {
          label: "Tours Requested",
          data: data2
            ? data2?.eventSummary.member.map((m) => m.totalCount)
            : [],
          borderColor: "rgb(52, 211, 153)",
          backgroundColor: "rgba(52, 211, 153, 0.5)",
          yAxisID: "y1",
        },
      ],
    };
  }, [data, data2]);

  React.useEffect(() => {
    if (!fetching && !fetching2) {
      const id = setTimeout(() => {
        setLastUpdated(new Date());
        executeQuery({ requestPolicy: "network-only" });
        executeQuery2({ requestPolicy: "network-only" });
      }, 30000);
      return () => clearTimeout(id);
    }
  }, [executeQuery, executeQuery2, fetching, fetching2]);

  return (
    <>
      <Loading show={!data && !error} />
      <Error error={error} />
      <Transition
        show={!!data}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
      >
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-2 my-2 sm:px-6 flex">
            <h3 className="text-lg leading-6 font-medium text-gray-900 grow">
              Tours
              <span className="text-xs text-gray-500">
                {" · "}
                Updated {distanceDateFormat(lastUpdated.toISOString())}
              </span>
            </h3>
          </div>
          <div className="p-2 border-t border-gray-200 grid grid-cols-1 gap-1">
            {data && (
              <div className="col-span-3 h-56">
                <Line
                  options={{
                    maintainAspectRatio: false,
                    showLine: true,
                    interaction: { mode: "index" },
                    scales: {
                      x: {
                        type: "time",
                      },
                      y: { position: "left" },
                      y1: { position: "right" },
                    },
                  }}
                  data={chartData}
                />
              </div>
            )}
          </div>
        </div>
      </Transition>
    </>
  );
};

export default DashboardTours;
